import { useState, useEffect, useRef } from 'react';
import { LoadingSpinner, PrimaryButton, SecondaryButton } from '@lib/common';
import { HeadingStatus, RequestStatus } from '@module/form';
import { useHeadingEditing, useHeadingForm, useHeadingFileUpload, useHeadingSave } from './hooks';
import { CardLayout, CardTitle, CardDescription, CardActions, StyledCardContainer, StyledGroupButton } from '../shared'; // Types

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var HeadingV2Edit = function HeadingV2Edit(_ref) {
  var _heading$id;

  var heading = _ref.heading,
      headingLabel = _ref.headingLabel,
      onUpdateHeading = _ref.onUpdateHeading,
      onSaveHeading = _ref.onSaveHeading,
      onCancel = _ref.onCancel,
      onDeleteHeading = _ref.onDeleteHeading,
      addHeadingFileStatus = _ref.addHeadingFileStatus,
      file = _ref.file,
      onResetAddHeadingFileUseCase = _ref.onResetAddHeadingFileUseCase,
      onShowingUploadHeadingFileModal = _ref.onShowingUploadHeadingFileModal,
      saveHeadingStatus = _ref.saveHeadingStatus,
      editorLabels = _ref.editorLabels;

  var _useState = useState(),
      openPopoverHeadingId = _useState[0],
      setOpenPopoverHeadingId = _useState[1];

  var hasInitializedNewHeading = useRef(false);
  var handleTitleFocusRef = useRef(function () {});

  if (!heading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }

  var headingId = (heading === null || heading === void 0 ? void 0 : (_heading$id = heading.id) === null || _heading$id === void 0 ? void 0 : _heading$id.toString()) || ''; // Hooks - tách logic ra các hooks riêng

  var editing = useHeadingEditing({
    heading: heading,
    headingLabel: headingLabel,
    readonly: false,
    onUpdateHeading: onUpdateHeading
  });
  var form = useHeadingForm({
    heading: heading,
    localTitle: editing.localTitle,
    localDescription: editing.localDescription,
    headingLabel: headingLabel,
    onUpdateHeading: onUpdateHeading,
    isEditing: true
  });

  var _useHeadingFileUpload = useHeadingFileUpload({
    addHeadingFileStatus: addHeadingFileStatus || RequestStatus.SUCCESS,
    file: file,
    onResetAddHeadingFileUseCase: onResetAddHeadingFileUseCase
  }),
      richTextEditorRef = _useHeadingFileUpload.richTextEditorRef;

  var _useHeadingSave = useHeadingSave({
    localTitle: editing.localTitle,
    localDescription: editing.localDescription,
    headingLabel: headingLabel,
    onSaveHeading: onSaveHeading,
    onUpdateHeading: onUpdateHeading
  }),
      handleSaveHeading = _useHeadingSave.handleSaveHeading; // Keep ref updated with latest function


  useEffect(function () {
    handleTitleFocusRef.current = editing.handleTitleFocus;
  }, [editing.handleTitleFocus]); // Auto focus title input when new heading is created and scroll to it

  useEffect(function () {
    // Check if heading is new (has status === New)
    var isNew = 'status' in heading && (heading === null || heading === void 0 ? void 0 : heading.status) === HeadingStatus.New;

    if (isNew && !hasInitializedNewHeading.current) {
      hasInitializedNewHeading.current = true; // Scroll to the new heading

      var scrollTimer = setTimeout(function () {
        var headingElement = document.getElementById(headingId);

        if (headingElement) {
          headingElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100); // Focus title input

      var focusTimer = setTimeout(function () {
        handleTitleFocusRef.current();
      }, 200);
      return function () {
        clearTimeout(scrollTimer);
        clearTimeout(focusTimer);
      };
    }

    if (!isNew) {
      hasInitializedNewHeading.current = false;
    }
  }, [heading, headingId]); // Handle cancel - reset local changes

  var handleCancel = function handleCancel() {
    editing.handleDescriptionCancel();
    onCancel();
  };

  return /*#__PURE__*/_jsxs(CardLayout, {
    isFocused: true,
    sectionId: headingId,
    formProps: {
      form: form.form,
      name: 'heading',
      initialValues: form.initialValues,
      onFieldsChange: form.onFieldsChange,
      preserve: false,
      onFinish: handleSaveHeading,
      layout: 'vertical'
    },
    children: [/*#__PURE__*/_jsxs(StyledCardContainer, {
      children: [/*#__PURE__*/_jsx(CardTitle, {
        tagLabel: (headingLabel === null || headingLabel === void 0 ? void 0 : headingLabel.heading) || 'HEADING',
        title: editing.localTitle,
        isEditing: true,
        isReadonly: false,
        placeholder: headingLabel === null || headingLabel === void 0 ? void 0 : headingLabel.placeholderHeadingTitle,
        onChange: function onChange(e) {
          return editing.handleTitleChange(e);
        },
        onBlur: editing.handleTitleBlur,
        onFocus: editing.handleTitleFocus,
        onKeyDown: editing.handleTitleKeyDown,
        onClick: editing.handleTitleFocus,
        autoFocus: false
      }), /*#__PURE__*/_jsx(CardActions, {
        isOnlyRemainingSection: false,
        actions: ['delete'],
        onDelete: function onDelete(e) {
          e === null || e === void 0 ? void 0 : e.stopPropagation();
          onDeleteHeading(headingId);
        },
        openPopoverId: openPopoverHeadingId,
        onPopoverChange: setOpenPopoverHeadingId,
        sectionId: headingId
      })]
    }), /*#__PURE__*/_jsx(CardDescription, {
      description: editing.localDescription,
      isEditing: true,
      isReadonly: false,
      placeholder: editing.localDescription ? headingLabel === null || headingLabel === void 0 ? void 0 : headingLabel.placeholderHeadingDescription : undefined,
      onChange: editing.handleDescriptionChange,
      hideButtons: true,
      editorLabels: editorLabels,
      onAddImage: function onAddImage() {
        return onShowingUploadHeadingFileModal === null || onShowingUploadHeadingFileModal === void 0 ? void 0 : onShowingUploadHeadingFileModal(headingId);
      },
      richTextEditorRef: richTextEditorRef
    }), /*#__PURE__*/_jsxs(StyledGroupButton, {
      children: [/*#__PURE__*/_jsx(SecondaryButton, {
        onClick: handleCancel,
        children: (headingLabel === null || headingLabel === void 0 ? void 0 : headingLabel.cancel) || 'Cancel'
      }), /*#__PURE__*/_jsx(PrimaryButton, {
        onClick: handleSaveHeading,
        loading: saveHeadingStatus === RequestStatus.EXECUTE,
        children: (headingLabel === null || headingLabel === void 0 ? void 0 : headingLabel.saveHeading) || 'Save'
      })]
    })]
  });
};